import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AccountType, ProviderRole } from '../types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: (AccountType | ProviderRole)[];
  requireLearner?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles, requireLearner }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600"></div>
    </div>
  );

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (requireLearner && !user.isLearner) {
    return <Navigate to="/learn" replace />;
  }

  const userGrants: (AccountType | ProviderRole)[] = [user.accountType, ...user.roles];
  if (allowedRoles && !allowedRoles.some(r => userGrants.includes(r))) {
    if (user.accountType === AccountType.ADMIN) return <>{children}</>; // Admins can see everything

    // Redirect based on role
    if (user.roles.includes(ProviderRole.PHYSIOTHERAPY)) return <Navigate to="/physio-dashboard" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
